import { useEffect, useRef, useState } from "react";

import {
  type InitiatePayinResponse,
  PayinRail,
  PaymentsPayinService,
} from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { savePseReturnContext } from "@/modules/dashboard/payins/utils/pseReturnContext";

/** How often the redirect URL is re-read while the backend backfills it. */
const REDIRECT_POLL_MS = 3_000;

/**
 * The rails whose collection instrument is a bank page the payer must be handed off to. Botón Bancolombia
 * shares PSE's whole lifecycle (its URL just arrives synchronously, so the poll short-circuits).
 */
const REDIRECT_RAILS: ReadonlySet<string> = new Set([PayinRail.PSE, PayinRail.BANCOLOMBIA_BUTTON]);

/** PSE early-rejection detail with its own mandated copy; anything else gets the generic message. */
export const PSE_DETAIL_EXCEEDED_LIMIT = "FAIL_EXCEEDEDLIMIT";

export interface UsePseRedirectOptions {
  /** The created payin. `null` before it exists, or when the rail is not PSE. */
  payin: InitiatePayinResponse | null;
  /** Bank display name, persisted for the return page before the document is replaced. */
  bankName: string;
  /** Operation reference, persisted alongside the bank name. */
  reference: string;
  /** The payin can no longer move — stop polling and never redirect. */
  isTerminal: boolean;
}

export interface UsePseRedirectResult {
  /** Network rejection detail surfaced by the polling read, for the failure screen's copy. */
  failureDetail: string | null;
}

/** The state the handoff decision is taken against. */
export interface PseHandoffState {
  /** Rail of the created payin, or `null` before it exists. */
  rail: string | null;
  /** The bank-authorization page, once the runtime has one. */
  redirectUrl: string | null;
  /** The payin can no longer move — it settled, was rejected, or failed. */
  isTerminal: boolean;
  /** A handoff already happened in this attempt. */
  alreadyHandedOff: boolean;
}

/**
 * May the payer be sent to their bank right now?
 *
 * Extracted from the effect because every clause is a way to lose or duplicate money, and none of them is
 * observable once `window.location` has changed: handing off twice would re-enter a flow the payer already
 * completed, and handing off a payin that already settled would send them to authorize a debit that is done.
 * Only the redirect rails (PSE, Botón Bancolombia) have a bank page at all.
 */
export function shouldHandOffToBank(state: PseHandoffState): boolean {
  if (!state.rail || !REDIRECT_RAILS.has(state.rail)) return false;
  if (!state.redirectUrl) return false;
  if (state.isTerminal) return false;
  return !state.alreadyHandedOff;
}

/** Should the redirect URL still be polled for? Same guards, minus the one about having it already. */
export function shouldPollForRedirect(state: Omit<PseHandoffState, "alreadyHandedOff">): boolean {
  return !!state.rail && REDIRECT_RAILS.has(state.rail) && !state.redirectUrl && !state.isTerminal;
}

/** What the poll has learned, stamped with the payin it was learned about. */
interface PolledRedirect {
  payinId: string;
  redirectUrl: string | null;
  failureDetail: string | null;
}

/**
 * Takes the payer to their bank, which PSE requires to happen automatically and in the same window — never
 * behind a button the payer has to find.
 *
 * The initiate response may not carry the URL yet, so a short poll recovers it once the backend backfills the
 * snapshot; the same read surfaces the rejection detail when the network refuses the registration instead.
 * Everything the return page needs is persisted immediately before navigating, because the redirect replaces
 * this document and the wizard's state goes with it.
 */
export function usePseRedirect({
  payin,
  bankName,
  reference,
  isTerminal,
}: UsePseRedirectOptions): UsePseRedirectResult {
  const rail = payin?.rail ?? null;
  const payinId = payin?.id ?? null;

  /**
   * Only what the POLL discovered, stamped with the payin it belongs to. Both halves of that matter.
   *
   * The stamp is what makes a retry safe: `resetFlow` mints a new payin inside the SAME mounted hook, and an
   * unkeyed URL left over from the previous attempt would send the payer to authorize a debit that is not the
   * one on screen — while `savePseReturnContext` writes the new payin's id and amount. Reading a stamp that
   * does not match is the same as having nothing.
   */
  const [polled, setPolled] = useState<PolledRedirect | null>(null);
  const forThisPayin = polled?.payinId === payinId ? polled : null;

  /**
   * The created payin's own URL is read straight from the prop rather than seeded into state, because a
   * `useState` initializer runs only on the hook's FIRST render: a hook mounted before the payin exists —
   * which is how `/app/payins` renders it, at the component root — would otherwise discard a `redirect_url`
   * that arrived with the initiate response and make the payer wait out a poll tick for something it already
   * had. The two surfaces are supposed to hand off identically.
   */
  const redirectUrl = payin?.redirect_url ?? forThisPayin?.redirectUrl ?? null;
  const failureDetail = forThisPayin?.failureDetail ?? null;

  /** The payin that was handed off — an id, not a flag, so a retry is not blocked by the previous attempt. */
  const handedOffRef = useRef<string | null>(null);

  const shouldPoll = shouldPollForRedirect({ rail, redirectUrl, isTerminal });

  useEffect(() => {
    if (!shouldPoll || !payinId) return;
    const timer = setInterval(() => {
      PaymentsPayinService.getPayin(payinId)
        .then((response) => {
          setPolled((current) => {
            // A tick only ever adds to what this payin's earlier ticks found; anything under another id is
            // a previous attempt's and is dropped rather than merged.
            const previous = current?.payinId === payinId ? current : null;
            return {
              payinId,
              redirectUrl: response.data.redirect_url ?? previous?.redirectUrl ?? null,
              failureDetail: response.data.pse?.failure_detail ?? previous?.failureDetail ?? null,
            };
          });
        })
        .catch(() => {
          // Transient — the next tick retries; the live-status hook still resolves the outcome.
        });
    }, REDIRECT_POLL_MS);
    return () => clearInterval(timer);
  }, [shouldPoll, payinId]);

  useEffect(() => {
    if (!payin || !redirectUrl) return;
    const allowed = shouldHandOffToBank({
      rail,
      redirectUrl,
      isTerminal,
      alreadyHandedOff: handedOffRef.current === payin.id,
    });
    if (!allowed) return;
    handedOffRef.current = payin.id;
    // Persisted BEFORE navigating, always: the redirect replaces this document and the wizard's state goes
    // with it, so the return page would otherwise have no bank name, amount or reference to show.
    savePseReturnContext(payin.id, {
      bankName,
      grossAmount: payin.gross_amount,
      currency: payin.currency,
      reference,
    });
    window.location.href = redirectUrl;
  }, [rail, payin, redirectUrl, isTerminal, bankName, reference]);

  // `handedOffRef` is deliberately not exposed: reading a ref during render is not a reliable signal, and
  // the caller already knows it is handing off — it is rendering the holding screen.
  return { failureDetail };
}
